import React from "react";
import {
  View,
  Modal,
  Text,
  TouchableOpacity,
  ScrollView,
  Image,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import i18n from "../../locales/i18n";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { FreeTierLimits } from "../../utils/freeTierUtils";

interface FreeTierModalProps {
  visible: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
  limits?: FreeTierLimits | null;
}

const FreeTierModal: React.FC<FreeTierModalProps> = ({
  visible,
  onClose,
  onUpgrade,
  limits,
}) => {
  const { locale } = useSelector((state: RootState) => state.app);

  const handleUpgrade = () => {
    // Navigate to contact page for upgrade inquiries
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Navigate to contact page for upgrade assistance
      onClose();
      router.push("/(drawer)/params/contact");
    }
  };

  return (
    <Modal visible={visible} animationType="fade" transparent={true}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          justifyContent: "center",
          alignItems: "center",
          padding: 20,
        }}
      >
        <View
          style={{
            backgroundColor: "white",
            borderRadius: 16,
            padding: 24,
            width: "100%",
            maxWidth: 400,
            maxHeight: "80%",
          }}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  color: "#333",
                  flex: 1,
                  textAlign: locale === "ar" ? "right" : "left",
                }}
              >
                {i18n.t("freeTierLimitExceeded")}
              </Text>
              <TouchableOpacity onPress={onClose} style={{ padding: 4 }}>
                <Ionicons name="close" size={24} color="#666" />
              </TouchableOpacity>
            </View>

            {/* Warning Icon */}
            <View
              style={{
                alignItems: "center",
                marginBottom: 20,
              }}
            >
              <View
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  backgroundColor: "#FFF3CD",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <Ionicons name="warning" size={40} color="#856404" />
              </View>
            </View>

            {/* Message */}
            <Text
              style={{
                fontSize: 16,
                color: "#666",
                textAlign: "center",
                marginBottom: 24,
                lineHeight: 24,
              }}
            >
              {i18n.t("freeTierLimitMessage")}
            </Text>

            {/* Current Limits Display */}
            {limits && (
              <View
                style={{
                  backgroundColor: "#F8F9FA",
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 24,
                }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    color: "#333",
                    marginBottom: 12,
                    textAlign: locale === "ar" ? "right" : "left",
                  }}
                >
                  {i18n.t("currentLimits")}
                </Text>

                <View style={{ gap: 8 }}>
                  <View
                    style={{
                      flexDirection: locale === "ar" ? "row-reverse" : "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 14, color: "#666" }}>
                      {i18n.t("productsLimit")}
                    </Text>
                    <Text style={{ fontSize: 14, color: "#333", fontWeight: "500" }}>
                      {limits.productCount}/51
                    </Text>
                  </View>

                  <View
                    style={{
                      flexDirection: locale === "ar" ? "row-reverse" : "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 14, color: "#666" }}>
                      {i18n.t("debtsLimit")}
                    </Text>
                    <Text style={{ fontSize: 14, color: "#333", fontWeight: "500" }}>
                      {limits.debtCount}/2
                    </Text>
                  </View>

                  <View
                    style={{
                      flexDirection: locale === "ar" ? "row-reverse" : "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text style={{ fontSize: 14, color: "#666" }}>
                      {i18n.t("invoicesLimit")}
                    </Text>
                    <Text style={{ fontSize: 14, color: "#333", fontWeight: "500" }}>
                      {limits.factureCount}/4
                    </Text>
                  </View>
                </View>
              </View>
            )}

            {/* Action Buttons */}
            <View style={{ gap: 12 }}>
              {/* Upgrade Button */}
              <TouchableOpacity onPress={handleUpgrade} style={{ width: "100%" }}>
                <LinearGradient
                  colors={["#259A9D", "#146C94", "#003789"]}
                  locations={[0, 0.28, 0.99]}
                  style={{
                    paddingHorizontal: 24,
                    paddingVertical: 16,
                    borderRadius: 12,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text
                    style={{
                      color: "white",
                      fontSize: 16,
                      fontWeight: "600",
                    }}
                  >
                    {i18n.t("upgradeNow")}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>

              {/* Continue Later Button */}
              <TouchableOpacity
                onPress={onClose}
                style={{
                  paddingHorizontal: 24,
                  paddingVertical: 16,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: "#DDD",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    color: "#666",
                    fontSize: 16,
                    fontWeight: "500",
                  }}
                >
                  {i18n.t("continueLater")}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

export default FreeTierModal;